import axios from 'axios';
import React from 'react';
import jwt from 'jsonwebtoken';
import { connect } from 'react-redux';
import { container } from 'op/container';
import { setupCache } from 'axios-cache-adapter';
import { compose, withHandlers, lifecycle, branch, renderNothing } from 'recompose';

import { withOnlineStatus } from 'op/components/offline';
import { set_auth_config } from "../../redux/reducers/actions";

export const withAxios = compose(
    withOnlineStatus,
    connect(({ auth }) => ({
        axios: auth.axios,
        cache: auth.cache
    }))
);

export const withAxiosHeaders = compose(
    withAxios,
    connect(({ auth }) => ({
        user: auth.user
    })
    ),
    withHandlers({
        setAxiosHeaders: ({ user, dispatch, online }) => () => {
            if (user && user.permission) {
                const { token, token_type, refresh_token } = user.auth;
                const cache = setupCache({
                    maxAge: 15 * 60 * 1000, // 15mins
                    invalidate: async (config, request) => {
                        if (request.clearCacheEntry) {
                            await config.store.removeItem(config.uuid)
                        }
                    }
                });

                const headers = {};
                if (user.auth.expires_at > Date.now()) {
                    headers['Authorization'] = `${token_type} ${token}`;
                }
                const axiosInstance = axios.create({
                    adapter: cache.adapter,
                    headers
                });

                const cacheInstance = cache.adapter;

                axiosInstance.interceptors.response.use(undefined, async err => {
                    const originalReq = err.config;
                    if (err.response.status === 401 && err.config && !err.config.__isRetryRequest) {
                        originalReq._retry = true;

                        const { data } = await axios.post('/api/auth/refreshToken', {
                            accessToken: token,
                            refreshToken: refresh_token
                        });

                        if (data.authError) {
                            dispatch(container.resolve('actions').clear_current_user());
                            throw new Error('Error');
                        }

                        const decoded = jwt.decode(data.accessToken);
                        const user = container.resolve('transformers').user(JSON.parse(decoded.user));

                        dispatch(container.resolve('actions').set_current_user({
                            ...user,
                            auth: {
                                token: data.accessToken,
                                token_type: 'Bearer',
                                expires_at: (decoded.exp * 1000),
                                refresh_token: data.refreshToken
                            }
                        }));

                        return axios({
                            ...originalReq,
                            headers: {
                                ...originalReq.headers,
                                Authorization: `${token_type} ${data.accessToken}`
                            }
                        });
                    }
                });

                dispatch(container.resolve('actions').set_axios_instace(axiosInstance));
                dispatch(container.resolve('actions').set_cache_instance(cacheInstance));
            } else {
                container.resolve('history').push(`/login?next=${window.location.pathname}`);
            }
        }
    }),
    lifecycle({
        componentDidMount() {
            this.props.setAxiosHeaders();
        },
        componentDidUpdate(prevProps) {
            if (prevProps.user !== this.props.user) {
                this.props.setAxiosHeaders();
            }
        },
    }),
    branch(
        ({ axios }) => !axios,
        renderNothing
    )
);

export const withLogout = compose(
    connect(),
    lifecycle({
        componentDidMount() {
            const { dispatch } = this.props;
            container.resolve('history').push('/login');
            dispatch(container.resolve('actions').clear_current_user());
        }
    })
);